import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import DataTable from '../components/DataTable';
import type { Column } from '../components/DataTable';
import { logger } from '../lib/logger';
import {
  deleteReportedContent,
  fetchContentPreview,
  fetchConversationMessages,
  fetchOfferPreview,
  fetchReportActions,
  fetchReports,
  reasonLabel,
  saveAdminNotes,
  suspendReported,
  targetLabel,
  updateReportStatus,
  warnReported,
  type ContentPreview,
  type OfferPreview,
  type ReportAction,
  type ReportRow,
  type ThreadMessage,
} from '../lib/reportService';

const statusLabels: Record<string, string> = {
  pending: 'Non traité',
  reviewed: 'En cours',
  resolved: 'Résolu',
  dismissed: 'Rejeté',
};

const statusClasses: Record<string, string> = {
  pending: 'bg-red-50 text-red-700 border border-red-200',
  reviewed: 'bg-amber-50 text-amber-700 border border-amber-200',
  resolved: 'bg-blue-50 text-blue-700 border border-blue-200',
  dismissed: 'bg-gray-50 text-gray-600 border border-gray-200',
};

const actionLabels: Record<string, string> = {
  warn: 'Avertissement',
  temp_suspend: 'Exclusion temporaire',
  ban: 'Radiation',
  delete_content: 'Contenu retiré',
  notify_reporter: 'Notification reporter',
  status: 'Changement de statut',
};

function personLink(person: ReportRow['reporter']) {
  if (person.role === 'candidate' && person.profileRowId) {
    return `/candidates/${person.profileRowId}`;
  }
  if (person.role === 'employer' && person.profileRowId) {
    return `/employers/${person.profileRowId}`;
  }
  return null;
}

const Reports: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [reports, setReports] = useState<ReportRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [selected, setSelected] = useState<ReportRow | null>(null);
  const [actions, setActions] = useState<ReportAction[]>([]);
  const [content, setContent] = useState<ContentPreview | null>(null);
  const [offer, setOffer] = useState<OfferPreview | null>(null);
  const [messages, setMessages] = useState<ThreadMessage[]>([]);
  const [notes, setNotes] = useState('');
  const [warnTitle, setWarnTitle] = useState('Avertissement — Power Jobs');
  const [warnBody, setWarnBody] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const rows = await fetchReports();
      setReports(rows);
      setError(null);
      return rows;
    } catch (err: any) {
      if (err?.message?.includes('does not exist') || err?.code === '42P01') {
        setError("La table reports n'existe pas encore dans la base.");
      } else {
        logger.error('load_reports', 'Reports', { error: String(err) });
        setError('Impossible de charger les signalements.');
      }
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openDetail = useCallback(async (row: ReportRow) => {
    setSelected(row);
    setNotes(row.adminNotes);
    setWarnBody('');
    setContent(null);
    setOffer(null);
    setMessages([]);
    setSearchParams({ focus: row.id }, { replace: true });
    try {
      const [acts, preview, offerPrev, thread] = await Promise.all([
        fetchReportActions(row.id),
        row.targetType === 'content' && row.targetId ? fetchContentPreview(row.targetId) : Promise.resolve(null),
        row.targetType === 'offer' && row.targetId ? fetchOfferPreview(row.targetId) : Promise.resolve(null),
        row.conversationId ? fetchConversationMessages(row.conversationId) : Promise.resolve([]),
      ]);
      setActions(acts);
      setContent(preview);
      setOffer(offerPrev);
      setMessages(thread);
    } catch (err) {
      logger.error('load_report_detail', 'Reports', { error: String(err) });
    }
  }, [setSearchParams]);

  useEffect(() => {
    const focus = searchParams.get('focus');
    if (!focus || selected?.id === focus || reports.length === 0) return;
    const row = reports.find((r) => r.id === focus);
    if (row) openDetail(row);
  }, [searchParams, reports, selected, openDetail]);

  const filtered = useMemo(() => {
    return reports.filter((r) => {
      const blob = `${r.details} ${r.reason} ${r.reporter.name} ${r.reported.name}`.toLowerCase();
      const matchSearch = search === '' || blob.includes(search.toLowerCase());
      const matchStatus = statusFilter === 'all' || r.status === statusFilter;
      const matchType = typeFilter === 'all' || r.targetType === typeFilter;
      return matchSearch && matchStatus && matchType;
    });
  }, [reports, search, statusFilter, typeFilter]);

  const refreshSelected = async (id: string) => {
    const rows = await load();
    const next = rows.find((r) => r.id === id);
    if (next) {
      setSelected(next);
      setNotes(next.adminNotes);
      setActions(await fetchReportActions(id));
    }
  };

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
      if (selected) await refreshSelected(selected.id);
    } catch (err) {
      alert(`Erreur: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  };

  const untreatedCount = reports.filter((r) => r.status === 'pending').length;

  const columns: Column<ReportRow>[] = [
    {
      key: 'date',
      header: 'Date',
      render: (r) => <span className="text-gray-500 text-xs">{r.createdAt}</span>,
    },
    {
      key: 'type',
      header: 'Cible',
      render: (r) => (
        <span className="inline-flex items-center px-2 py-0.5 bg-gray-50 text-gray-700 text-xs font-medium rounded-md border border-gray-200">
          {targetLabel(r.targetType)}
        </span>
      ),
    },
    {
      key: 'reason',
      header: 'Motif',
      render: (r) => (
        <span className="text-xs text-gray-700">{reasonLabel(r.reason)}</span>
      ),
    },
    {
      key: 'description',
      header: 'Détail',
      render: (r) => (
        <p className="text-xs text-gray-600 max-w-xs truncate" title={r.details}>
          {r.details || '—'}
        </p>
      ),
    },
    {
      key: 'people',
      header: 'Reporter / Signalé',
      render: (r) => (
        <div className="text-xs text-gray-600">
          <p>{r.reporter.name}</p>
          <p className="text-gray-400">→ {r.reported.name}</p>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Statut',
      render: (r) => (
        <span className={`inline-block px-2.5 py-1 text-xs font-medium rounded-full ${statusClasses[r.status] || 'bg-gray-100 text-gray-500'}`}>
          {statusLabels[r.status] || r.status}
        </span>
      ),
    },
  ];

  const filterSlot = (
    <div className="flex items-center gap-2">
      <select
        value={typeFilter}
        onChange={(e) => setTypeFilter(e.target.value)}
        className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20 focus:border-[#00244F]"
      >
        <option value="all">Toutes les cibles</option>
        <option value="profile">Profil</option>
        <option value="content">Contenu</option>
        <option value="message">Message</option>
        <option value="offer">Offre</option>
      </select>
      <select
        value={statusFilter}
        onChange={(e) => setStatusFilter(e.target.value)}
        className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20 focus:border-[#00244F]"
      >
        <option value="all">Tous les statuts</option>
        <option value="pending">Non traité</option>
        <option value="reviewed">En cours</option>
        <option value="resolved">Résolu</option>
        <option value="dismissed">Rejeté</option>
      </select>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#00244F]/20 border-t-[#00244F] rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl border border-amber-200 p-8 text-center">
        <span className="text-4xl block mb-3">📋</span>
        <p className="text-gray-700 font-medium">{error}</p>
        <p className="text-sm text-gray-400 mt-2">Cette fonctionnalité sera disponible une fois la table créée.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-xl border border-red-200 p-4 flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center text-2xl">
          🚩
        </div>
        <div>
          <p className="text-2xl font-bold text-red-600">{untreatedCount}</p>
          <p className="text-sm text-gray-500">signalements non traités</p>
        </div>
        <div className="ml-auto flex items-center gap-4 text-sm">
          <div className="text-center">
            <p className="font-semibold text-amber-600">{reports.filter((r) => r.status === 'reviewed').length}</p>
            <p className="text-xs text-gray-400">en cours</p>
          </div>
          <div className="text-center">
            <p className="font-semibold text-blue-600">{reports.filter((r) => r.status === 'resolved').length}</p>
            <p className="text-xs text-gray-400">résolus</p>
          </div>
          <div className="text-center">
            <p className="font-semibold text-gray-600">{reports.filter((r) => r.status === 'dismissed').length}</p>
            <p className="text-xs text-gray-400">rejetés</p>
          </div>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filtered}
        keyExtractor={(r) => r.id}
        searchable
        searchPlaceholder="Rechercher par motif, nom, description..."
        onSearch={setSearch}
        filterSlot={filterSlot}
        pageSize={10}
        onRowClick={openDetail}
      />

      {selected && (
        <div className="fixed inset-0 z-40 flex justify-end bg-black/30" onClick={() => { setSelected(null); setSearchParams({}, { replace: true }); }}>
          <div
            className="w-full max-w-xl bg-white h-full overflow-y-auto shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  {targetLabel(selected.targetType)} · {reasonLabel(selected.reason)}
                </p>
                <p className="text-xs text-gray-400">{selected.createdAt}</p>
              </div>
              <button
                onClick={() => { setSelected(null); setSearchParams({}, { replace: true }); }}
                className="text-gray-400 hover:text-gray-700 text-sm"
              >
                Fermer
              </button>
            </div>

            <div className="p-5 space-y-5">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <PersonCard label="Reporter" person={selected.reporter} />
                <PersonCard label="Signalé" person={selected.reported} />
              </div>

              {selected.details && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">Précisions</p>
                  <p className="text-sm text-gray-800 mt-1 whitespace-pre-wrap">{selected.details}</p>
                </div>
              )}

              {content && (
                <div className="border border-gray-200 rounded-xl p-3">
                  <p className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold mb-2">Contenu</p>
                  {content.mediaType === 'image' && content.url ? (
                    <img src={content.url} alt="" className="w-full rounded-lg max-h-64 object-cover" />
                  ) : content.url ? (
                    <video src={content.url} poster={content.thumbnailUrl || undefined} controls className="w-full rounded-lg max-h-64 bg-black" />
                  ) : content.thumbnailUrl ? (
                    <img src={content.thumbnailUrl} alt="" className="w-full rounded-lg max-h-64 object-cover" />
                  ) : (
                    <p className="text-xs text-gray-400">Aperçu indisponible</p>
                  )}
                  <p className="text-xs text-gray-500 mt-2">Statut : {content.status} · {content.source}</p>
                  <Link to={`/videos?focus=${selected.targetId}`} className="text-xs text-[#00244F] underline">
                    Ouvrir en modération
                  </Link>
                </div>
              )}

              {offer && (
                <div className="border border-gray-200 rounded-xl p-3">
                  <p className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold mb-1">Offre</p>
                  <p className="text-sm font-medium text-gray-800">{offer.title}</p>
                  <p className="text-xs text-gray-500">{offer.company} · {offer.city} · {offer.status}</p>
                  <Link to={`/offers?focus=${offer.id}`} className="text-xs text-[#00244F] underline">
                    Voir l’offre
                  </Link>
                </div>
              )}

              {messages.length > 0 && (
                <div className="border border-gray-200 rounded-xl p-3">
                  <p className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold mb-2">
                    Lecture interne du fil
                  </p>
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {messages.map((m) => (
                      <div
                        key={m.id}
                        className={`text-xs rounded-lg px-2.5 py-2 ${
                          m.id === selected.targetId ? 'bg-red-50 border border-red-200' : 'bg-gray-50'
                        }`}
                      >
                        <p className="text-gray-400 mb-0.5">{m.createdAt} · {m.senderId.slice(0, 8)}</p>
                        <p className="text-gray-800 whitespace-pre-wrap">{m.body}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <p className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold mb-1">Notes admin</p>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20"
                />
                <button
                  disabled={busy}
                  onClick={() => run(() => saveAdminNotes(selected.id, notes))}
                  className="mt-2 px-3 py-1.5 text-xs font-medium text-[#00244F] bg-[#00244F]/5 rounded-md"
                >
                  Enregistrer les notes
                </button>
              </div>

              <div className="flex flex-wrap gap-2">
                {selected.status === 'pending' && (
                  <button
                    disabled={busy}
                    onClick={() => run(() => updateReportStatus(selected, 'reviewed', notes))}
                    className="px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-md"
                  >
                    Prendre en charge
                  </button>
                )}
                {selected.status !== 'resolved' && (
                  <button
                    disabled={busy}
                    onClick={() => run(() => updateReportStatus(selected, 'resolved', notes))}
                    className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-md"
                  >
                    Résoudre
                  </button>
                )}
                {selected.status !== 'dismissed' && (
                  <button
                    disabled={busy}
                    onClick={() => run(() => updateReportStatus(selected, 'dismissed', notes))}
                    className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-md"
                  >
                    Rejeter
                  </button>
                )}
              </div>

              <div className="border-t border-gray-100 pt-4 space-y-3">
                <p className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">Actions CGU</p>
                <div>
                  <input
                    value={warnTitle}
                    onChange={(e) => setWarnTitle(e.target.value)}
                    className="w-full mb-2 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                    placeholder="Titre de l’avertissement"
                  />
                  <textarea
                    value={warnBody}
                    onChange={(e) => setWarnBody(e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                    placeholder="Message d’avertissement"
                  />
                  <button
                    disabled={busy || !warnBody.trim()}
                    onClick={() => run(() => warnReported(selected, warnTitle, warnBody))}
                    className="mt-2 px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 rounded-md"
                  >
                    Avertir
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    disabled={busy}
                    onClick={() => run(() => suspendReported(selected, 24))}
                    className="px-3 py-1.5 text-xs font-medium text-orange-700 bg-orange-50 rounded-md"
                  >
                    Exclusion 24 h
                  </button>
                  <button
                    disabled={busy}
                    onClick={() => run(() => suspendReported(selected, 168))}
                    className="px-3 py-1.5 text-xs font-medium text-orange-700 bg-orange-50 rounded-md"
                  >
                    Exclusion 7 j
                  </button>
                  <button
                    disabled={busy}
                    onClick={() => run(() => suspendReported(selected, 720))}
                    className="px-3 py-1.5 text-xs font-medium text-orange-700 bg-orange-50 rounded-md"
                  >
                    Exclusion 30 j
                  </button>
                  <button
                    disabled={busy}
                    onClick={() => {
                      if (!confirm(`Radier définitivement ${selected.reported.name} ?`)) return;
                      return run(() => suspendReported(selected));
                    }}
                    className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-md"
                  >
                    Radier
                  </button>
                  {selected.targetType !== 'profile' && (
                    <button
                      disabled={busy}
                      onClick={() => {
                        if (!confirm('Retirer le contenu signalé ?')) return;
                        return run(() => deleteReportedContent(selected));
                      }}
                      className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-md"
                    >
                      Supprimer le contenu
                    </button>
                  )}
                </div>
              </div>

              <div>
                <p className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold mb-2">Historique</p>
                {actions.length === 0 ? (
                  <p className="text-xs text-gray-400">Aucune action pour l’instant.</p>
                ) : (
                  <ul className="space-y-1.5">
                    {actions.map((a) => (
                      <li key={a.id} className="text-xs text-gray-600 bg-gray-50 rounded-md px-2.5 py-1.5">
                        <span className="font-medium">{actionLabels[a.action] || a.action}</span>
                        <span className="text-gray-400"> · {a.createdAt}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function PersonCard({ label, person }: { label: string; person: ReportRow['reporter'] }) {
  const href = personLink(person);
  return (
    <div className="bg-gray-50 rounded-lg p-3">
      <p className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">{label}</p>
      {href ? (
        <Link to={href} className="text-sm font-medium text-[#00244F] hover:underline">
          {person.name}
        </Link>
      ) : (
        <p className="text-sm font-medium text-gray-800">{person.name}</p>
      )}
      <p className="text-xs text-gray-400">{person.role || '—'}</p>
    </div>
  );
}

export default Reports;
